import React, { useState, useCallback, useRef, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  TextField, 
  Button, 
  Box, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link,
  CircularProgress,
  Alert,
  Autocomplete
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import Settings from './Settings';
import { useTheme } from '../contexts/ThemeContext';

const MTGPricer = ({ signOut }) => {
  const { darkMode, setDarkModeValue } = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [uniqueSets, setUniqueSets] = useState([]);
  const [selectedSet, setSelectedSet] = useState(null);
  const [availableCollectorNumbers, setAvailableCollectorNumbers] = useState([]);
  const [selectedCollectorNumber, setSelectedCollectorNumber] = useState('');
  const [selectedCardVersion, setSelectedCardVersion] = useState(null);
  const [selectedLanguage] = useState('en'); // Always use English
  const [selectedFinish, setSelectedFinish] = useState('nonfoil');
  const [, setAvailableLanguages] = useState([]); // Keep setter for compatibility but don't use the value
  const [availableFinishes, setAvailableFinishes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Autocomplete state
  const [suggestions, setSuggestions] = useState([]);
  const [autocompleteLoading, setAutocompleteLoading] = useState(false);
  
  // TCGPlayer pricing state
  const [pricing, setPricing] = useState({
    lpMarketPrice: null,
    loading: false,
    error: null,
    productId: null
  });
  
  // Settings state
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState({
    version: 1.01,
    basePriceModifier: 1.15, // 115% of market price as starting point
    conditionMultipliers: {
      'NM': 1.0,
      'EX': 0.9,
      'VG': 0.75,
      'G': 0.5
    },
    buyCashMultipliers: {
      'NM': 0.85,
      'EX': 0.77,
      'VG': 0.60,
      'G': 0.30
    },
    creditMultiplier: 1.3,
    // Tiered buying system
    bulkThreshold: 3.0,    // Cards under $3 use "BULK" system
    highEndThreshold: 200.0, // Cards over $200 use "HIGH END" system
    // Pricing tiers (applied after condition multipliers, before buy multipliers)
    pricingTiers: [
      { name: 'Very Low', lowerBound: 3.0, multiplier: 0.08 },
      { name: 'Low', lowerBound: 4.0, multiplier: 0.25 },
      { name: 'Mid', lowerBound: 8.0, multiplier: 0.40 },
      { name: 'Mid-High', lowerBound: 20.0, multiplier: 0.45 },
      { name: 'High', lowerBound: 30.0, multiplier: 0.50 }
    ],
    darkMode: false
  });
  
  // Conservative cache with size limit
  const cacheRef = useRef(new Map());
  const maxCacheSize = 50; // Limit cache to 50 entries to prevent memory bloat
  const debounceTimeoutRef = useRef(null);
  const searchInputRef = useRef(null);

  // API configuration
  const API_BASE_URL = 'https://ftukxywiue.execute-api.us-east-2.amazonaws.com/dev/api';
  
  // Helper function to normalize search terms for flexible matching
  const normalizeSearchTerm = (term) => {
    // Remove all punctuation, spaces, and convert to lowercase
    return term.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
  };

  // Language mapping removed - always use English

  // Load settings from localStorage on component mount with robust error handling
  useEffect(() => {
    const loadSettings = () => {
      try {
        const CURRENT_VERSION = 1.01;
        const savedSettings = localStorage.getItem('mtgPricerSettings');
        if (savedSettings) {
          const parsedSettings = JSON.parse(savedSettings);
          
          // Check version - if outdated or missing, clear and use new defaults
          if (!parsedSettings.version || parsedSettings.version < CURRENT_VERSION) {
            console.log(`Settings version outdated (${parsedSettings.version || 'none'} < ${CURRENT_VERSION}), resetting to new defaults`);
            localStorage.removeItem('mtgPricerSettings');
            return; // Use default state values
          }
          
          // Validate that the loaded settings have the expected structure
          if (parsedSettings.conditionMultipliers && 
              parsedSettings.buyCashMultipliers && 
              typeof parsedSettings.creditMultiplier === 'number') {
            
            console.log('Settings loaded successfully from localStorage');
            // Ensure all fields have default values if not present
            const settingsWithDefaults = {
              ...parsedSettings,
              version: CURRENT_VERSION,
              basePriceModifier: typeof parsedSettings.basePriceModifier === 'number' ? parsedSettings.basePriceModifier : 1.15,
              bulkThreshold: typeof parsedSettings.bulkThreshold === 'number' ? parsedSettings.bulkThreshold : 3.0,
              highEndThreshold: typeof parsedSettings.highEndThreshold === 'number' ? parsedSettings.highEndThreshold : 200.0,
              pricingTiers: Array.isArray(parsedSettings.pricingTiers) ? parsedSettings.pricingTiers : [
                { name: 'Very Low', lowerBound: 3.0, multiplier: 0.08 },
                { name: 'Low', lowerBound: 4.0, multiplier: 0.25 },
                { name: 'Mid', lowerBound: 8.0, multiplier: 0.40 },
                { name: 'Mid-High', lowerBound: 20.0, multiplier: 0.45 },
                { name: 'High', lowerBound: 30.0, multiplier: 0.50 }
              ],
              darkMode: typeof parsedSettings.darkMode === 'boolean' ? parsedSettings.darkMode : false
            };
            setSettings(settingsWithDefaults);
          } else {
            console.warn('Invalid settings structure in localStorage, using defaults');
          }
        } else {
          console.log('No saved settings found, using defaults');
        }
      } catch (error) {
        console.error('Failed to load settings from localStorage:', error);
        console.log('Using default settings due to error');
      }
    };

    loadSettings();
  }, []);

  // Sync settings dark mode with theme context dark mode
  useEffect(() => {
    setSettings(prevSettings => ({
      ...prevSettings,
      darkMode: darkMode
    }));
  }, [darkMode]);

  // Focus search input after search completes
  useEffect(() => {
    // Focus the input when searchTerm becomes empty and we're not loading
    if (!loading && searchTerm === '' && searchInputRef.current) {
      // Small delay to ensure the input is ready
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    }
  }, [loading, searchTerm]);

  // Helper function to find the applicable pricing tier
  const findPricingTier = (price) => {
    // Sort tiers by lowerBound in descending order to find the highest applicable tier
    const sortedTiers = [...settings.pricingTiers].sort((a, b) => b.lowerBound - a.lowerBound);
    
    for (const tier of sortedTiers) {
      if (price >= tier.lowerBound) {
        return tier;
      }
    }
    
    // If no tier matches, return null (shouldn't happen with proper setup)
    return null;
  };

  // Calculate pricing grid from TCGPlayer base price using current settings
  const calculatePricingGrid = (tcgplayerBasePrice) => {
    if (!tcgplayerBasePrice) return null;
    
    const conditions = ['NM', 'EX', 'VG', 'G'];
    const pricingGrid = [];
    
    // Step 1: Apply base price modifier to TCGPlayer market price
    const adjustedBasePrice = tcgplayerBasePrice * settings.basePriceModifier;
    
    // Step 2: Determine if this is a BULK or HIGH END card based on adjusted base price
    const isBulk = adjustedBasePrice < settings.bulkThreshold;
    const isHighEnd = adjustedBasePrice > settings.highEndThreshold;
    
    conditions.forEach(condition => {
      // SELL PRICE FLOW: Base → Base Modifier → Price Multipliers
      const sellPrice = adjustedBasePrice * settings.conditionMultipliers[condition];
      
      let buyCash, buyCredit;
      
      if (isBulk) {
        // BULK system - show "BULK" instead of calculated prices
        buyCash = 'BULK';
        buyCredit = 'BULK';
      } else if (isHighEnd) {
        // HIGH END system - show "HIGH END" instead of calculated prices
        buyCash = 'HIGH END';
        buyCredit = 'HIGH END';
      } else {
        // BUY(CASH) FLOW: Base → Base Modifier → Pricing Tier → Buy(Cash) Multipliers → Tier Multiplier
        
        // Step 3: Find pricing tier based on adjusted base price
        const pricingTier = findPricingTier(adjustedBasePrice);
        
        // Step 4: Apply buy(cash) multiplier to adjusted base price
        const baseBuyPrice = adjustedBasePrice * settings.buyCashMultipliers[condition];
        
        // Step 5: Apply pricing tier multiplier to buy price
        buyCash = pricingTier ? baseBuyPrice * pricingTier.multiplier : baseBuyPrice;
        
        // Step 6: Apply credit multiplier to final buy cash price
        buyCredit = buyCash * settings.creditMultiplier;
      }
      
      pricingGrid.push({
        condition,
        price: sellPrice,  // This is now the "Sell Price"
        buyCash,
        buyCredit,
        isBulk,
        isHighEnd
      });
    });
    
    return pricingGrid;
  };

  // TCGPlayer Pricing Functions
  const extractTCGPlayerProductId = (tcgplayerUri) => {
    if (!tcgplayerUri) return null;
    
    // Handle URL-encoded URIs from Scryfall partner links
    const decodedUri = decodeURIComponent(tcgplayerUri);
    const match = decodedUri.match(/product\/(\d+)/);
    return match ? match[1] : null;
  };

  const getTCGPlayerCondition = (selectedFinish) => {
    return selectedFinish === 'foil' ? 'Foil' : 'Normal';
  };



  const fetchTCGPlayerPricing = useCallback(async (cardVersion, finish) => {
    if (!cardVersion?.purchase_uris?.tcgplayer) {
      setPricing({
        lpMarketPrice: null,
        loading: false,
        error: 'No TCGPlayer link available',
        productId: null
      });
      return;
    }

    const productId = extractTCGPlayerProductId(cardVersion.purchase_uris.tcgplayer);
    if (!productId) {
      setPricing({
        lpMarketPrice: null,
        loading: false,
        error: 'Could not extract product ID',
        productId: null
      });
      return;
    }

    setPricing(prev => ({ ...prev, loading: true, error: null }));

    try {
      const response = await fetch(`${API_BASE_URL}/pricing?product_id=${productId}`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      // Extract market price inline to avoid dependency issues
      const targetCondition = getTCGPlayerCondition(finish);
      const condition = data.pricing?.results?.find(r => r.subTypeName === targetCondition);
      const lpPrice = condition?.marketPrice || null;

      setPricing({
        lpMarketPrice: lpPrice,
        loading: false,
        error: lpPrice === null ? 'Market pricing not available' : null,
        productId: productId
      });

    } catch (error) {
      console.error('TCGPlayer pricing error:', error);
      setPricing({
        lpMarketPrice: null,
        loading: false,
        error: error.message,
        productId: productId
      });
    }
  }, [API_BASE_URL]);

  // Fetch pricing only when Set + Collector Number + Finish are all determined
  useEffect(() => {
    if (selectedSet && selectedCollectorNumber && selectedFinish && selectedCardVersion) {
      fetchTCGPlayerPricing(selectedCardVersion, selectedFinish);
    } else {
      // Clear pricing when selections are incomplete
      setPricing({
        lpMarketPrice: null,
        loading: false,
        error: null,
        productId: null
      });
    }
  }, [selectedSet, selectedCollectorNumber, selectedFinish, selectedCardVersion, fetchTCGPlayerPricing]);

  // Check if all selections are made (unused but kept for potential future use)
  // const allSelectionsComplete = selectedCardVersion && selectedLanguage && selectedFinish;

  // Conservative autocomplete with LRU cache management
  const fetchAutocomplete = useCallback(async (query) => {
    if (query.length < 3) {
      setSuggestions([]);
      return;
    }

    // Check cache first
    const cache = cacheRef.current;
    if (cache.has(query)) {
      // Move to end (most recently used)
      const cached = cache.get(query);
      cache.delete(query);
      cache.set(query, cached);
      setSuggestions(cached);
      return;
    }

    setAutocompleteLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/autocomplete?q=${encodeURIComponent(query)}`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      const results = data.suggestions || [];

      // Implement LRU cache with size limit
      if (cache.size >= maxCacheSize) {
        // Remove oldest entry (first in map)
        const firstKey = cache.keys().next().value;
        cache.delete(firstKey);
      }
      
      cache.set(query, results);
      setSuggestions(results);
      
    } catch (err) {
      console.error('Autocomplete error:', err);
      setSuggestions([]); // Fail silently for autocomplete
    } finally {
      setAutocompleteLoading(false);
    }
  }, [API_BASE_URL, maxCacheSize]);

  // Debounced autocomplete with 750ms delay
  const debouncedAutocomplete = useCallback((query) => {
    // Clear previous timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set new timeout
    debounceTimeoutRef.current = setTimeout(() => {
      fetchAutocomplete(query);
    }, 750); // 750ms delay as requested
  }, [fetchAutocomplete]);

  // Handle autocomplete input change
  const handleAutocompleteChange = (newValue) => {
    setSearchTerm(newValue);
    
    if (newValue) {
      debouncedAutocomplete(newValue);
    } else {
      setSuggestions([]);
    }
  };

  // Helper function to group cards by sets and extract unique sets
  const processSearchResults = (cards) => {
    const setMap = new Map();
    const allCards = cards;
    
    // Group by set code and extract unique sets
    cards.forEach(card => {
      const setCode = card.set_code || '';
      const setName = card.set_name || '';
      const releasedAt = card.released_at || '';
      
      if (!setMap.has(setCode)) {
        setMap.set(setCode, {
          code: setCode,
          name: setName,
          released_at: releasedAt,
          cards: [],
          collectorNumbers: new Set()
        });
      }
      
      const setInfo = setMap.get(setCode);
      setInfo.cards.push(card);
      
      // Clean collector number (remove foil symbols)
      const collectorNum = (card.collector_number || '').replace(/[★☆✦]/g, '');
      if (collectorNum) {
        setInfo.collectorNumbers.add(collectorNum);
      }
    });
    
    // Convert to arrays and sort by release date (newest first)
    const uniqueSets = Array.from(setMap.values()).sort((a, b) => {
      // Sort by release date (newest first), then by name if dates are equal
      const dateA = new Date(a.released_at || '1900-01-01');
      const dateB = new Date(b.released_at || '1900-01-01');
      if (dateA.getTime() !== dateB.getTime()) {
        return dateB.getTime() - dateA.getTime(); // Newest first
      }
      return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    });
    
    return { allCards, uniqueSets, setMap };
  };

  // Core search logic that can be called from multiple places
  const performSearch = async (term) => {
    if (!term.trim()) return;

    setLoading(true);
    setError('');
    
    try {
      // First, try to find a matching suggestion from our autocomplete cache
      // This handles the case where user types "birdsofparadise" and we have "Birds of Paradise" in suggestions
      const normalizedTerm = normalizeSearchTerm(term);
      let matchedSuggestion = null;
      
      // Check if any of our cached suggestions match when normalized
      for (const suggestion of suggestions) {
        if (normalizeSearchTerm(suggestion) === normalizedTerm) {
          matchedSuggestion = suggestion;
          break;
        }
      }
      
      // If we found a matching suggestion, use that for the search
      const searchTerm = matchedSuggestion || term;
      
      // Search for cards using our API
      const searchResponse = await fetch(`${API_BASE_URL}/search?q=${encodeURIComponent(searchTerm)}`);
      const searchData = await searchResponse.json();
      
      if (!searchResponse.ok) {
        throw new Error(searchData.error || 'Failed to search for cards');
      }

      if (!searchData.cards || searchData.cards.length === 0) {
        // Set a special state for no results found
        setSearchResults([]);
        setUniqueSets([]);
        setSelectedSet(null);
        setSelectedCollectorNumber('');
        setSelectedCardVersion(null);
        setSelectedFinish('nonfoil');
        setAvailableCollectorNumbers([]);
        setAvailableLanguages([]);
        setAvailableFinishes([]);
        setError('No card found');
        return;
      }

      // Process search results and group by sets
      const { allCards, uniqueSets } = processSearchResults(searchData.cards);
      
      setSearchResults(allCards);
      setUniqueSets(uniqueSets);
      
      // Clear autocomplete suggestions when search results are loaded
      setSuggestions([]);
      // Clear any pending autocomplete requests
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      
      // Auto-select the newest set (first in the sorted array)
      if (uniqueSets.length > 0) {
        const newestSet = uniqueSets[0];
        setSelectedSet(newestSet);
        
        // Auto-select the lowest collector number for the newest set
        const collectorNumbers = Array.from(newestSet.collectorNumbers).sort((a, b) => {
          const numA = parseInt(a, 10);
          const numB = parseInt(b, 10);
          if (!isNaN(numA) && !isNaN(numB)) {
            return numA - numB;
          }
          return a.localeCompare(b);
        });
        
        setAvailableCollectorNumbers(collectorNumbers);
        
        if (collectorNumbers.length > 0) {
          const lowestCollectorNumber = collectorNumbers[0];
          setSelectedCollectorNumber(lowestCollectorNumber);
          
          // Find all cards matching this set and collector number
          const matchingCards = allCards.filter(card => {
            const cardSetCode = card.set_code || '';
            const cardCollectorNum = (card.collector_number || '').replace(/[★☆✦]/g, '');
            return cardSetCode === newestSet.code && cardCollectorNum === lowestCollectorNumber;
          });
          
          if (matchingCards.length > 0) {
            // Extract available languages and finishes
            const languages = new Set();
            const finishes = new Set();
            
            matchingCards.forEach(card => {
              languages.add(card.lang || 'en');
              if (card.finishes) {
                card.finishes.forEach(finish => finishes.add(finish));
              }
            });
            
            const langArray = Array.from(languages);
            const finishArray = Array.from(finishes);
            
            setAvailableLanguages(langArray);
            setAvailableFinishes(finishArray);
            
            // Set smart defaults (language is always 'en')
            const defaultLang = 'en';
            const defaultFinish = finishArray.includes('nonfoil') ? 'nonfoil' : finishArray[0];
            
            setSelectedFinish(defaultFinish);
            
            // Find the specific card version that matches defaults
            const defaultCard = matchingCards.find(card => {
              const langMatch = card.lang === defaultLang;
              const finishMatch = card.finishes && card.finishes.includes(defaultFinish);
              return langMatch && finishMatch;
            });
            
            setSelectedCardVersion(defaultCard || matchingCards[0]);
          }
        } else {
          // Reset if no collector numbers
          setSelectedCollectorNumber('');
          setSelectedCardVersion(null);
          setSelectedFinish('nonfoil');
          setAvailableLanguages([]);
          setAvailableFinishes([]);
        }
      } else {
        // Reset selections if no sets found
        setSelectedSet(null);
        setSelectedCollectorNumber('');
        setSelectedCardVersion(null);
        setSelectedFinish('nonfoil');
        setAvailableCollectorNumbers([]);
        setAvailableLanguages([]);
        setAvailableFinishes([]);
      }
      
    } catch (err) {
      console.error('Search error:', err);
      setError(err.message || 'Failed to search for cards');
      setSearchResults([]);
      setSelectedCardVersion(null);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get autocomplete suggestions with Promise
  const getAutocompleteSuggestions = async (query) => {
    try {
      const response = await fetch(`${API_BASE_URL}/autocomplete?q=${encodeURIComponent(query)}`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      return data.suggestions || [];
    } catch (error) {
      console.error('Autocomplete error:', error);
      return [];
    }
  };

  // Form submit handler
  const handleSearch = async (e) => {
    e.preventDefault();
    const termToSearch = searchTerm.trim();
    
    if (!termToSearch) return;
    
    setLoading(true);
    
    try {
      // Always fetch fresh autocomplete suggestions for the current term
      const currentSuggestions = await getAutocompleteSuggestions(termToSearch);
      
      if (currentSuggestions.length > 0) {
        // Find the best match - prioritize exact prefix matches, then alphabetical
        const exactMatch = currentSuggestions.find(suggestion => 
          suggestion.toLowerCase().startsWith(termToSearch.toLowerCase())
        );
        const bestMatch = exactMatch || [...currentSuggestions].sort()[0]; // First alphabetically if no exact match
        
        await performSearch(bestMatch);
      } else {
        // No suggestions found, try direct search
        await performSearch(termToSearch);
      }
    } catch (error) {
      console.error('Search error:', error);
      // Fallback to direct search
      await performSearch(termToSearch);
    }
    
    // Clear the search box after search
    setSearchTerm('');
    setLoading(false);
  };

  // Handle autocomplete selection (when user clicks on a suggestion)
  const handleAutocompleteSelect = async (event, newValue) => {
    if (newValue && typeof newValue === 'string' && newValue.trim()) {
      // Automatically trigger search
      await performSearch(newValue);
      // Clear the search box after successful search
      setSearchTerm('');
    }
  };

  const handleClear = () => {
    setSearchTerm('');
    setSearchResults([]);
    setUniqueSets([]);
    setSelectedSet(null);
    setSelectedCollectorNumber('');
    setSelectedCardVersion(null);
    // Language is always 'en' - no need to set
    setSelectedFinish('nonfoil');
    setAvailableCollectorNumbers([]);
    setAvailableLanguages([]);
    setAvailableFinishes([]);
    setError('');
    setSuggestions([]);
    
    // Clear any pending autocomplete
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    console.log('Form cleared - reset to starting point');
  };

  const handleSetSelect = (setCode) => {
    const selectedSetInfo = uniqueSets.find(set => set.code === setCode);
    setSelectedSet(selectedSetInfo);
    
    if (selectedSetInfo) {
      // Set available collector numbers for this set
      const collectorNumbers = Array.from(selectedSetInfo.collectorNumbers).sort((a, b) => {
        // Sort collector numbers numerically when possible
        const numA = parseInt(a, 10);
        const numB = parseInt(b, 10);
        if (!isNaN(numA) && !isNaN(numB)) {
          return numA - numB;
        }
        return a.localeCompare(b);
      });
      
      setAvailableCollectorNumbers(collectorNumbers);
      
      // Automatically select the lowest collector number and process it
      if (collectorNumbers.length > 0) {
        const lowestCollectorNumber = collectorNumbers[0];
        setSelectedCollectorNumber(lowestCollectorNumber);
        
        // Find all cards matching this set and collector number
        const matchingCards = searchResults.filter(card => {
          const cardSetCode = card.set_code || '';
          const cardCollectorNum = (card.collector_number || '').replace(/[★☆✦]/g, '');
          return cardSetCode === selectedSetInfo.code && cardCollectorNum === lowestCollectorNumber;
        });
        
        if (matchingCards.length > 0) {
          // Extract available languages and finishes
          const languages = new Set();
          const finishes = new Set();
          
          matchingCards.forEach(card => {
            languages.add(card.lang || 'en');
            if (card.finishes) {
              card.finishes.forEach(finish => finishes.add(finish));
            }
          });
          
          const langArray = Array.from(languages);
          const finishArray = Array.from(finishes);
          
          setAvailableLanguages(langArray);
          setAvailableFinishes(finishArray);
          
          // Set smart defaults (language is always 'en')
          const defaultLang = 'en';
          const defaultFinish = finishArray.includes('nonfoil') ? 'nonfoil' : finishArray[0];
          
          setSelectedFinish(defaultFinish);
          
          // Find the specific card version that matches defaults
          const defaultCard = matchingCards.find(card => {
            const langMatch = card.lang === defaultLang;
            const finishMatch = card.finishes && card.finishes.includes(defaultFinish);
            return langMatch && finishMatch;
          });
          
          setSelectedCardVersion(defaultCard || matchingCards[0]);
        }
      } else {
        // Reset subsequent selections if no collector numbers
        setSelectedCollectorNumber('');
        setSelectedCardVersion(null);
// Language is always 'en' - no need to set
        setSelectedFinish('nonfoil');
        setAvailableLanguages([]);
        setAvailableFinishes([]);
      }
    }
  };

  const handleCollectorNumberSelect = (collectorNumber) => {
    setSelectedCollectorNumber(collectorNumber);
    
    if (selectedSet && collectorNumber) {
      // Find all cards matching this set and collector number
      const matchingCards = searchResults.filter(card => {
        const cardSetCode = card.set_code || '';
        const cardCollectorNum = (card.collector_number || '').replace(/[★☆✦]/g, '');
        return cardSetCode === selectedSet.code && cardCollectorNum === collectorNumber;
      });
      
      if (matchingCards.length > 0) {
        // Extract available languages and finishes
        const languages = new Set();
        const finishes = new Set();
        
        matchingCards.forEach(card => {
          languages.add(card.lang || 'en');
          if (card.finishes) {
            card.finishes.forEach(finish => finishes.add(finish));
          }
        });
        
        const langArray = Array.from(languages);
        const finishArray = Array.from(finishes);
        
        setAvailableLanguages(langArray);
        setAvailableFinishes(finishArray);
        
        // Set smart defaults (language is always 'en')
        const defaultLang = 'en';
        const defaultFinish = finishArray.includes('nonfoil') ? 'nonfoil' : finishArray[0];
        
        setSelectedFinish(defaultFinish);
        
        // Find the specific card version that matches defaults
        const defaultCard = matchingCards.find(card => {
          const langMatch = card.lang === defaultLang;
          const finishMatch = card.finishes && card.finishes.includes(defaultFinish);
          return langMatch && finishMatch;
        });
        
        setSelectedCardVersion(defaultCard || matchingCards[0]);
      }
    }
  };

  // Get the specific card variant based on selected language and finish
  const getCurrentCardVariant = () => {
    if (!selectedSet || !selectedCollectorNumber || !selectedLanguage || !selectedFinish) {
      return selectedCardVersion;
    }
    
    // Find the exact variant that matches all selections
    const matchingVariant = searchResults.find(card => {
      const cardSetCode = card.set_code || '';
      const cardCollectorNum = (card.collector_number || '').replace(/[★☆✦]/g, '');
      const langMatch = card.lang === selectedLanguage;
      const finishMatch = card.finishes && card.finishes.includes(selectedFinish);
      
      return cardSetCode === selectedSet.code && 
             cardCollectorNum === selectedCollectorNumber && 
             langMatch && 
             finishMatch;
    });
    
    return matchingVariant || selectedCardVersion;
  };

  // Handler for finish changes
  const handleFinishChange = () => {
    if (selectedSet && selectedCollectorNumber) {
      const currentVariant = getCurrentCardVariant();
      if (currentVariant) {
        setSelectedCardVersion(currentVariant);
      }
    }
  };

  return (
    <Container maxWidth="lg" sx={{ 
      py: { xs: 2, md: 4 }, 
      px: { xs: 1, sm: 2, md: 3 },
      position: 'relative', 
      minHeight: '100vh' 
    }}>
      {/* Settings Button - Bottom right corner of screen */}
      <Button
        onClick={() => setShowSettings(true)}
        variant="outlined"
        sx={{
          position: 'fixed',
          bottom: 0,
          right: 0,
          m: 2,
          color: 'primary.main',
          borderColor: 'primary.main',
          backgroundColor: 'background.paper',
          zIndex: 1000,
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'action.hover',
            boxShadow: 1
          }
        }}
        startIcon={<SettingsIcon />}
      >
        Settings
      </Button>

      
      <Box component="form" onSubmit={handleSearch} sx={{ mb: 4 }}>
        <Box sx={{ 
          display: 'flex', 
          gap: { xs: 1, md: 2 }, 
          alignItems: 'center',
          flexDirection: { xs: 'column', sm: 'row' }
        }}>
          <Autocomplete
            fullWidth
            freeSolo
            options={suggestions}
            value={searchTerm}
            onInputChange={(event, newValue) => handleAutocompleteChange(newValue)}
            onChange={handleAutocompleteSelect}
            loading={autocompleteLoading}
            disabled={loading}
            filterOptions={(options, { inputValue }) => {
              if (!inputValue) return options;
              
              const normalizedInput = normalizeSearchTerm(inputValue);
              
              // First, show exact matches
              const exactMatches = options.filter(option => 
                option.toLowerCase() === inputValue.toLowerCase()
              );
              
              // Then, show normalized matches (for flexible search)
              const normalizedMatches = options.filter(option => {
                const normalizedOption = normalizeSearchTerm(option);
                return normalizedOption.includes(normalizedInput) && 
                       !exactMatches.includes(option);
              });
              
              // Finally, show partial matches
              const partialMatches = options.filter(option => 
                option.toLowerCase().includes(inputValue.toLowerCase()) &&
                !exactMatches.includes(option) &&
                !normalizedMatches.includes(option)
              );
              
              return [...exactMatches, ...normalizedMatches, ...partialMatches];
            }}
            renderInput={(params) => {
              return (
                <TextField
                  {...params}
                  inputRef={(input) => {
                    searchInputRef.current = input;
                  }}
                  variant="outlined"
                  label="Search for a Magic card..."
                  placeholder="Enter card name (e.g., Lightning Bolt)"
                sx={{ 
                  bgcolor: 'background.paper',
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                    '& fieldset': {
                      borderColor: 'divider',
                    },
                    '&:hover fieldset': {
                      borderColor: 'text.secondary',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'text.secondary',
                    '&.Mui-focused': {
                      color: 'primary.main',
                    },
                  },
                  '& input': {
                    color: 'text.primary',
                  },
                  '& input::placeholder': {
                    color: 'text.secondary',
                    opacity: 0.7,
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {autocompleteLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
              );
            }}
            sx={{ 
              '& .MuiAutocomplete-listbox': {
                maxHeight: '200px', // Limit dropdown height
                bgcolor: 'background.paper',
              },
              '& .MuiAutocomplete-paper': {
                bgcolor: 'background.paper',
                color: 'text.primary',
              },
              '& .MuiAutocomplete-option': {
                color: 'text.primary',
                '&[aria-selected="true"]': {
                  bgcolor: 'action.selected',
                },
                '&.Mui-focused': {
                  bgcolor: 'action.hover',
                },
              },
              '& .MuiAutocomplete-popupIndicator': {
                color: 'text.secondary',
              },
              '& .MuiAutocomplete-clearIndicator': {
                color: 'text.secondary',
              },
            }}
          />
          <Box sx={{ 
            display: 'flex', 
            gap: { xs: 1, md: 2 }, 
            width: { xs: '100%', sm: 'auto' },
            justifyContent: { xs: 'stretch', sm: 'flex-start' }
          }}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading || !searchTerm.trim()}
              sx={{ 
                px: { xs: 2, md: 3 },
                minWidth: { xs: 'auto', md: 120 },
                flex: { xs: 1, sm: '0 0 auto' },
                backgroundColor: '#1976d2',
                '&:hover': {
                  backgroundColor: '#1565c0'
                }
              }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : 'Search'}
            </Button>
            <Button
              variant="outlined"
              onClick={handleClear}
              size="large"
              sx={{
                px: { xs: 2, md: 3 },
                minWidth: { xs: 'auto', md: 120 },
                flex: { xs: 1, sm: '0 0 auto' },
                color: '#ffffff',
                borderColor: '#808080',
                backgroundColor: 'rgba(64, 64, 64, 0.4)',
                '&:hover': {
                  color: '#ffffff',
                  borderColor: '#a0a0a0',
                  backgroundColor: 'rgba(96, 96, 96, 0.6)'
                }
              }}
            >
              Clear
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Error Display */}
      {error && (
        <Box sx={{ mb: 3 }}>
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        </Box>
      )}

      {/* Search Results - Show after search */}
      {searchResults.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, textAlign: 'center', color: 'text.primary' }}>
            Found {searchResults.length} versions of <span style={{ fontWeight: 'bold' }}>"{searchResults[0].name}"</span>
          </Typography>
          
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'flex-start', 
            gap: { xs: 2, md: 3 },
            flexDirection: { xs: 'column', lg: 'row' }
          }}>
            {/* Left side - Controls and Pricing */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 2, 
              width: { xs: '100%', lg: 500 }, 
              flex: '0 0 auto' 
            }}>
              {/* Dropdowns Section */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Set Selection */}
              <FormControl fullWidth>
                <InputLabel 
                  id="set-select-label" 
                  shrink 
                  sx={{ 
                    color: 'text.secondary',
                    '&.Mui-focused': {
                      color: 'primary.main',
                    }
                  }}
                >
                  Set
                </InputLabel>
                <Select
                  labelId="set-select-label"
                  id="set-select"
                  value={selectedSet?.code || ''}
                  label="Set"
                  onChange={(e) => handleSetSelect(e.target.value)}
                  displayEmpty
                  sx={{ 
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'divider',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'text.secondary',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                    '& .MuiSelect-icon': {
                      color: 'text.secondary'
                    }
                  }}
                >
                  <MenuItem value="" disabled sx={{ color: 'text.disabled' }}>
                    Select a Set
                  </MenuItem>
                  {uniqueSets.map((set) => (
                    <MenuItem key={set.code} value={set.code}>
                      {set.name} ({set.code.toUpperCase()})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Collector Number Selection */}
              {selectedSet && (
                <FormControl fullWidth>
                  <InputLabel 
                    id="collector-select-label" 
                    shrink 
                    sx={{ 
                      color: 'text.secondary',
                      '&.Mui-focused': {
                        color: 'primary.main',
                      }
                    }}
                  >
                    Collector Number
                  </InputLabel>
                  <Select
                    labelId="collector-select-label"
                    id="collector-select"
                    value={selectedCollectorNumber}
                    label="Collector Number"
                    onChange={(e) => handleCollectorNumberSelect(e.target.value)}
                    displayEmpty
                    sx={{ 
                      bgcolor: 'background.paper',
                      color: 'text.primary',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'divider',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'text.secondary',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                      },
                      '& .MuiSelect-icon': {
                        color: 'text.secondary'
                      }
                    }}
                  >
                    <MenuItem value="" disabled sx={{ color: 'grey' }}>
                      Select a Collector Number
                    </MenuItem>
                    {availableCollectorNumbers.map((number) => (
                      <MenuItem key={number} value={number}>
                        #{number}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}


              {/* Finish Selection */}
              {selectedCollectorNumber && availableFinishes.length > 0 && (
                <FormControl fullWidth>
                  <InputLabel id="finish-select-label" shrink sx={{ 
                    color: 'text.secondary',
                    '&.Mui-focused': {
                      color: 'primary.main',
                    }
                  }}>
                    Finish
                  </InputLabel>
                  <Select
                    labelId="finish-select-label"
                    id="finish-select"
                    value={selectedFinish}
                    label="Finish"
                    onChange={(e) => {
                      setSelectedFinish(e.target.value);
                      handleFinishChange();
                    }}
                    sx={{ 
                      bgcolor: 'background.paper',
                      color: 'text.primary',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'divider',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'text.secondary',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                      },
                      '& .MuiSelect-icon': {
                        color: 'text.secondary'
                      }
                    }}
                  >
                    {availableFinishes.map((finish) => (
                      <MenuItem key={finish} value={finish}>
                        {finish.charAt(0).toUpperCase() + finish.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              </Box>
              
              {/* Pricing Grid - Positioned under dropdowns */}
              {selectedSet && selectedCollectorNumber && (
                <Box sx={{ 
                  bgcolor: 'background.paper', 
                  p: { xs: 2, md: 3 }, 
                  borderRadius: 2, 
                  mt: 2,
                  border: '1px solid',
                  borderColor: 'divider'
                }}>
                  {/* Status message */}
                  {!selectedFinish ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, textAlign: 'center' }}>
                      Select finish to load pricing
                    </Typography>
                  ) : pricing.loading ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, textAlign: 'center' }}>
                      Loading pricing data...
                    </Typography>
                  ) : pricing.error ? (
                    <Typography variant="body2" sx={{ color: 'error.main', mb: 2, textAlign: 'center' }}>
                      {pricing.error}
                    </Typography>
                  ) : pricing.lpMarketPrice !== null ? (
                    <Typography variant="body2" sx={{ color: 'text.primary', mb: 2, textAlign: 'center' }}>
                      Base Price: ${pricing.lpMarketPrice.toFixed(2)} (Market - {selectedFinish === 'foil' ? 'Foil' : 'Non-foil'})
                    </Typography>
                  ) : (
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, textAlign: 'center' }}>
                      Ready to load pricing
                    </Typography>
                  )}
                      
                  <TableContainer component={Paper} sx={{ bgcolor: 'background.paper' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                          <TableCell sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>Condition</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>Sell Price</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>Buy (Cash)</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>Buy (Credit)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(() => {
                          const conditions = ['NM', 'EX', 'VG', 'G'];
                          const hasData = pricing.lpMarketPrice !== null && !pricing.loading && !pricing.error;
                          const pricingGrid = hasData ? calculatePricingGrid(pricing.lpMarketPrice) : null;
                          
                          return conditions.map((condition) => {
                            const rowData = pricingGrid?.find(row => row.condition === condition);
                            const isLoading = pricing.loading;
                            const hasError = pricing.error;
                            
                            return (
                              <TableRow key={condition}>
                                <TableCell sx={{ fontWeight: condition === 'NM' ? 'bold' : 'normal' }}>
                                  {condition}
                                </TableCell>
                                <TableCell align="right" sx={{ 
                                  fontWeight: condition === 'NM' ? 'bold' : 'normal',
                                  color: condition === 'NM' ? 'primary.main' : 'text.primary'
                                }}>
                                  {isLoading ? (
                                    <CircularProgress size={16} />
                                  ) : hasError ? (
                                    <Typography variant="caption" color="error">N/A</Typography>
                                  ) : rowData ? (
                                    `$${rowData.price.toFixed(2)}`
                                  ) : (
                                    <Typography variant="caption" color="text.secondary">-</Typography>
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  {isLoading ? (
                                    <CircularProgress size={16} />
                                  ) : hasError ? (
                                    <Typography variant="caption" color="error">N/A</Typography>
                                  ) : rowData ? (
                                    typeof rowData.buyCash === 'string' ? (
                                      <Typography variant="body2" sx={{ 
                                        fontWeight: 'bold',
                                        color: rowData.isBulk ? 'warning.main' : 'secondary.main'
                                      }}>
                                        {rowData.buyCash}
                                      </Typography>
                                    ) : (
                                      `$${rowData.buyCash.toFixed(2)}`
                                    )
                                  ) : (
                                    <Typography variant="caption" color="text.secondary">-</Typography>
                                  )}
                                </TableCell>
                                <TableCell align="right" sx={{ color: hasData ? 'success.main' : 'text.primary', fontWeight: 'medium' }}>
                                  {isLoading ? (
                                    <CircularProgress size={16} />
                                  ) : hasError ? (
                                    <Typography variant="caption" color="error">N/A</Typography>
                                  ) : rowData ? (
                                    typeof rowData.buyCredit === 'string' ? (
                                      <Typography variant="body2" sx={{ 
                                        fontWeight: 'bold',
                                        color: rowData.isBulk ? 'warning.main' : 'secondary.main'
                                      }}>
                                        {rowData.buyCredit}
                                      </Typography>
                                    ) : (
                                      `$${rowData.buyCredit.toFixed(2)}`
                                    )
                                  ) : (
                                    <Typography variant="caption" color="text.secondary">-</Typography>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          });
                        })()}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 2, textAlign: 'center' }}>
                    Powered by TCGPlayer API
                  </Typography>
              
                  {/* TCGPlayer Link - Show only when card is selected and has link */}
                  {selectedCardVersion?.purchase_uris?.tcgplayer && (
                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                      <Link
                        href={selectedCardVersion.purchase_uris.tcgplayer}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          display: 'inline-block',
                          padding: '8px 16px',
                          backgroundColor: 'secondary.main',
                          color: 'secondary.contrastText',
                          textDecoration: 'none',
                          borderRadius: '6px',
                          fontWeight: 'bold',
                          fontSize: { xs: '12px', md: '14px' },
                          '&:hover': {
                            backgroundColor: 'secondary.dark',
                            textDecoration: 'none'
                          }
                        }}
                      >
                        View on TCGPlayer.com
                      </Link>
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            {/* Right side - Card Image */}
            <Box sx={{ 
              flex: '0 0 auto', 
              display: 'flex', 
              justifyContent: 'center',
              width: { xs: '100%', lg: 'auto' }
            }}>
              <Box sx={{ 
                width: { xs: 280, sm: 320, md: 350 }, 
                height: { xs: 390, sm: 447, md: 490 }, 
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid grey',
                maxWidth: '100%',
                mx: 'auto'
              }}>
                {(() => {
                  const currentVariant = getCurrentCardVariant();
                  return currentVariant?.image_url ? (
                    <img 
                      src={currentVariant.image_url}
                      alt={`${currentVariant.name} - ${currentVariant.set_name}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: '6px'
                      }}
                      onError={(e) => {
                        // Fallback to MTG card back if image fails to load
                        e.target.src = '/MTGCardBack.png';
                      }}
                    />
                  ) : (
                    <img 
                      src="/MTGCardBack.png"
                      alt="Magic: The Gathering Card Back"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: '6px'
                      }}
                    />
                  );
                })()}
              </Box>
            </Box>
          </Box>
        </Box>
      )}



      {/* Card Info */}
      {selectedCardVersion && (
        <Box sx={{ 
          textAlign: 'center', 
          mb: { xs: 2, md: 3 },
          px: { xs: 1, md: 0 }
        }}>
          <Typography variant="body2" sx={{ color: 'text.primary' }}>
            {selectedCardVersion.name}
            {selectedCardVersion.mana_cost && ` | Mana Cost: ${selectedCardVersion.mana_cost}`}
            {selectedCardVersion.type_line && ` | Type: ${selectedCardVersion.type_line}`}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
            {`Set: ${selectedCardVersion.set_name} (${selectedCardVersion.set_code.toUpperCase()})`}
            {selectedCardVersion.collector_number && ` #${selectedCardVersion.collector_number}`}
            {selectedCardVersion.rarity && ` | ${selectedCardVersion.rarity.charAt(0).toUpperCase() + selectedCardVersion.rarity.slice(1)}`}
            {selectedCardVersion.artist && ` | Artist: ${selectedCardVersion.artist}`}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
            {`Language: English`}
            {selectedFinish && ` | Finish: ${selectedFinish.charAt(0).toUpperCase() + selectedFinish.slice(1)}`}
            {(() => {
              const currentVariant = getCurrentCardVariant();
              return currentVariant ? (
                <>
                  {currentVariant.promo && ' | PROMO'}
                  {currentVariant.full_art && ' | Full Art'}
                </>
              ) : null;
            })()}
          </Typography>
        </Box>
      )}



      {/* Settings Modal */}
      {showSettings && (
        <Settings
          currentSettings={settings}
          onSave={(newSettings) => {
            setSettings(newSettings);
            // Sync dark mode with theme context
            if (typeof newSettings.darkMode === 'boolean' && newSettings.darkMode !== darkMode) {
              setDarkModeValue(newSettings.darkMode);
            }
            setShowSettings(false);
          }}
          onClose={() => setShowSettings(false)}
          signOut={signOut}
        />
      )}

      {/* Version Display - Bottom left corner */}
      <Typography
        variant="caption"
        sx={{
          position: 'fixed',
          bottom: 8,
          left: 8,
          color: 'text.secondary',
          fontSize: '0.75rem',
          zIndex: 999
        }}
      >
        Version {settings.version}
      </Typography>

    </Container>
  );
};

export default MTGPricer;
